import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getLiveOddsList,
  getOpenBetBoardGame,
} from "../../actions/gameSettings";
import { getActiveBetLegs } from "../../../utility/activeBetGameLookup";
import { resolveOpenBetBoardGames } from "../../../utility/openBetBoardGames";
import { OPEN_BETS_REFRESH_EVENT } from "../../../context/BetSlipProvider";
import { useLiveBoardSocket } from "../../../hooks/useLiveBoardSocket";
import { mergeLiveFieldsIntoGame } from "../../../utility/liveBoardScoreRefresh";
import WagerableMatchBoard from "./WagerableMatchBoard";
import { isDTS } from "../../../utility/platform";
import {
  getTerm,
  getEntryTypeLabel,
  getFeaturedMarketLabel,
} from "../../../utility/complianceCopy";
import "./openbetExpand.css";

function ActiveBetMatchExpand({ bet }) {
  const [legGames, setLegGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const legs = useMemo(() => getActiveBetLegs(bet), [bet]);

  const loadBoardGames = useCallback(async () => {
    if (!legs.length) {
      setLegGames([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setFetchError(null);

    try {
      const { status, result } = await getLiveOddsList();
      const bulkGames = status === true && Array.isArray(result) ? result : [];

      const resolved = await resolveOpenBetBoardGames(
        legs,
        bulkGames,
        getOpenBetBoardGame
      );

      setLegGames(resolved);
      setFetchError(null);
    } catch {
      setFetchError("Unable to load live match board.");
      setLegGames(legs.map((leg) => null));
    } finally {
      setLoading(false);
    }
  }, [legs]);

  useLiveBoardSocket((liveGames) => {
    setLegGames((prev) => {
      if (!prev?.length) return prev;
      return prev.map((game) =>
        game ? mergeLiveFieldsIntoGame(game, liveGames) : game
      );
    });
  });

  useEffect(() => {
    loadBoardGames();
  }, [loadBoardGames, bet?.ticketId]);

  useEffect(() => {
    const onRefresh = () => loadBoardGames();
    window.addEventListener(OPEN_BETS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(OPEN_BETS_REFRESH_EVENT, onRefresh);
  }, [loadBoardGames]);

  // The stored entry type is data ("Parlay Bet" on some orders); translate it
  // for display rather than rendering it raw.
  const entryTypeLabel = bet?.betType ? getEntryTypeLabel(bet.betType, isDTS()) : "";
  const headerTitle =
    legs.length === 1
      ? legGames[0]?.sport_title ||
        legs[0]?.sport_title ||
        legs[0]?.title ||
        entryTypeLabel ||
        "Match"
      : entryTypeLabel || (isDTS() ? getTerm("entry") : "Bet");
  const eventLabel = `${legs.length} Event${legs.length === 1 ? "" : "s"}`;

  if (!bet || legs.length === 0) return null;

  return (
    <div
      className="openbet-expand-panel customAccordionWrapper"
      data-testid="active-bet-match-expand"
    >
      <div className="openbet-expand-header">
        <div className="openbet-expand-header__sport">
          <span className="openbet-expand-header__title">{headerTitle}</span>
          <span className="openbet-expand-header__meta">{eventLabel}</span>
        </div>
        <div className="openbet-expand-header__actions">
          <span className="openbet-expand-header__markets">
            {`SPREADS · ${String(
              getFeaturedMarketLabel("h2h", isDTS())
            ).toUpperCase()} · TOTALS`}
          </span>
          <button
            type="button"
            className="openbet-expand-refresh-btn"
            disabled={loading}
            onClick={loadBoardGames}
          >
            {loading ? "Refreshing…" : "Refresh board"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="openbet-expand-status mb-0">Loading match board…</p>
      ) : null}

      {!loading && fetchError ? (
        <p className="openbet-expand-status openbet-expand-status--warn mb-2">
          {fetchError}
        </p>
      ) : null}

      {legs.map((legBet, index) => (
        <WagerableMatchBoard
          key={
            legBet.gameId ||
            legBet.id ||
            `${legBet.homeTeam}-${legBet.awayTeam}-${index}`
          }
          legBet={legBet}
          game={legGames[index]}
          legIndex={index}
          totalLegs={legs.length}
          betType={bet.betType}
        />
      ))}
    </div>
  );
}

export default ActiveBetMatchExpand;
